import React from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_ON_PRIMARY_FIXED,
    BRAND_OUTLINE_VARIANT,
    BRAND_PRIMARY_CONTAINER,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGH,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../brandDashboardTokens';
import { BrandAdHubMetaAccountFormDialog } from './BrandAdHubMetaAccountFormDialog';
import { useAdHubMetaConnection } from './useAdHubMetaConnection';

export interface BrandAdHubMetaConnectSectionProps {
    brandId: string | undefined;
    /** Compact layout for embedding in boost modal */
    compact?: boolean;
    /**
     * `section` — settings-style block.
     * `socialCard` — matches Brand Social Integration platform cards (onboarding).
     */
    variant?: 'section' | 'socialCard';
    onReadyChange?: (ready: boolean) => void;
}

export function BrandAdHubMetaConnectSection({
    brandId,
    compact = false,
    variant = 'section',
    onReadyChange,
}: BrandAdHubMetaConnectSectionProps) {
    const {
        loading,
        connecting,
        disconnecting,
        connection,
        error,
        pendingOAuthCode,
        connected,
        readyForClientAds,
        startConnect,
        completeConnect,
        cancelPendingConnect,
        disconnect,
        refresh,
    } = useAdHubMetaConnection(brandId);

    React.useEffect(() => {
        onReadyChange?.(readyForClientAds);
    }, [onReadyChange, readyForClientAds]);

    if (!brandId) {
        return (
            <Alert severity="info" sx={{ bgcolor: `${BRAND_LUXE_PRIMARY}14`, color: 'rgba(255,255,255,0.75)' }}>
                Select a brand to manage Meta ad account connections.
            </Alert>
        );
    }

    const formDialog = (
        <BrandAdHubMetaAccountFormDialog
            open={Boolean(pendingOAuthCode)}
            submitting={connecting}
            onClose={cancelPendingConnect}
            onSubmit={completeConnect}
        />
    );

    if (variant === 'socialCard') {
        const statusLabel = readyForClientAds ? 'Connected' : connected ? 'Setup needed' : null;
        const subtitle = readyForClientAds
            ? connection?.platform_account_name || connection?.platform_account_id || 'Ready for Reels boosts & AdHub'
            : connected
            ? 'Add Facebook Page ID to finish setup'
            : 'Ads account for campaign report boosts';

        return (
            <>
                <Box
                    sx={{
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        p: 3,
                        borderRadius: 3,
                        border: '1px solid transparent',
                        transition: 'all 0.2s',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        '&:hover': {
                            bgcolor: BRAND_SURFACE_CONTAINER,
                            borderColor: 'rgba(255,201,101,0.15)',
                            '& .meta-link-btn': connected ? {} : { bgcolor: BRAND_LUXE_PRIMARY, color: '#392700' },
                        },
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                            <CampaignOutlinedIcon sx={{ fontSize: 24, color: '#fff' }} />
                        </Box>
                        {statusLabel ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    color: readyForClientAds ? BRAND_LUXE_SECONDARY : BRAND_LUXE_PRIMARY,
                                }}>
                                <CheckCircleIcon sx={{ fontSize: 14 }} />
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.1em',
                                    }}>
                                    {statusLabel}
                                </Typography>
                            </Box>
                        ) : (
                            <LinkIcon sx={{ fontSize: 20, color: '#adaaaa' }} />
                        )}
                    </Box>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#fff', mb: 0.5 }}>
                        Meta ad account
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#adaaaa', mb: 3, flex: 1 }}>{subtitle}</Typography>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                            <CircularProgress size={22} sx={{ color: BRAND_LUXE_PRIMARY }} />
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 'auto' }}>
                            {connected ? (
                                <>
                                    <Button
                                        className="meta-link-btn"
                                        fullWidth
                                        onClick={() => void startConnect()}
                                        disabled={connecting || disconnecting}
                                        sx={{
                                            py: 1.25,
                                            borderRadius: 999,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 14,
                                            textTransform: 'none',
                                            bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                                            color: '#fff',
                                        }}>
                                        Reconnect
                                    </Button>
                                    <Button
                                        fullWidth
                                        onClick={() => void disconnect()}
                                        disabled={connecting || disconnecting}
                                        startIcon={
                                            disconnecting ? (
                                                <CircularProgress size={14} sx={{ color: 'inherit' }} />
                                            ) : (
                                                <LinkOffIcon sx={{ fontSize: 16 }} />
                                            )
                                        }
                                        sx={{
                                            py: 1.25,
                                            borderRadius: 999,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 14,
                                            textTransform: 'none',
                                            bgcolor: 'rgba(38,38,38,0.5)',
                                            color: '#adaaaa',
                                        }}>
                                        Disconnect
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    className="meta-link-btn"
                                    fullWidth
                                    onClick={() => void startConnect()}
                                    disabled={connecting}
                                    startIcon={
                                        connecting ? (
                                            <CircularProgress size={16} sx={{ color: 'inherit' }} />
                                        ) : undefined
                                    }
                                    sx={{
                                        py: 1.25,
                                        borderRadius: 999,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 14,
                                        textTransform: 'none',
                                        bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                                        color: '#fff',
                                        '&.Mui-disabled': {
                                            bgcolor: connecting ? BRAND_SURFACE_CONTAINER_HIGH : undefined,
                                            color: connecting ? '#fff' : 'rgba(255,255,255,0.3)',
                                        },
                                    }}>
                                    {connecting ? 'Connecting...' : 'Connect Meta'}
                                </Button>
                            )}
                        </Box>
                    )}

                    {error ? <Typography sx={{ fontSize: 12, color: '#ff8a65', mt: 1.5 }}>{error}</Typography> : null}
                </Box>
                {formDialog}
            </>
        );
    }

    return (
        <Box
            sx={{
                p: compact ? 2 : 3,
                borderRadius: 3,
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                border: `1px solid ${BRAND_OUTLINE_VARIANT}44`,
            }}>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: compact ? 16 : 20,
                    color: '#fff',
                    mb: 0.5,
                }}>
                Meta ad account
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', mb: 2, lineHeight: 1.5 }}>
                Connect the brand Meta ad account used for campaign report boosts and AdHub client placement. Same
                connection as the AdHub module.
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={28} sx={{ color: BRAND_LUXE_PRIMARY }} />
                </Box>
            ) : (
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: BRAND_SURFACE_CONTAINER,
                        border: `1px solid ${BRAND_OUTLINE_VARIANT}33`,
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 2,
                    }}>
                    <Box>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', fontSize: 14 }}>
                            {readyForClientAds
                                ? 'Connected'
                                : connected
                                ? 'Connected — add Facebook Page ID'
                                : 'Not connected'}
                        </Typography>
                        {connection?.platform_account_name || connection?.platform_account_id ? (
                            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>
                                {connection.platform_account_name || connection.platform_account_id}
                                {connection.platform_account_id ? ` · ${connection.platform_account_id}` : ''}
                            </Typography>
                        ) : (
                            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', mt: 0.5 }}>
                                Required before boosting posts as Meta Reels.
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {connected ? (
                            <>
                                <Button
                                    onClick={() => void startConnect()}
                                    disabled={connecting || disconnecting}
                                    startIcon={<LinkIcon />}
                                    sx={{
                                        textTransform: 'none',
                                        color: BRAND_LUXE_PRIMARY,
                                        border: `1px solid ${BRAND_LUXE_PRIMARY}55`,
                                        borderRadius: 999,
                                        px: 2,
                                    }}>
                                    Reconnect
                                </Button>
                                <Button
                                    onClick={() => void disconnect()}
                                    disabled={connecting || disconnecting}
                                    startIcon={
                                        disconnecting ? (
                                            <CircularProgress size={14} sx={{ color: 'inherit' }} />
                                        ) : (
                                            <LinkOffIcon />
                                        )
                                    }
                                    sx={{
                                        textTransform: 'none',
                                        color: 'rgba(255,255,255,0.65)',
                                        borderRadius: 999,
                                        px: 2,
                                    }}>
                                    Disconnect
                                </Button>
                            </>
                        ) : (
                            <Button
                                onClick={() => void startConnect()}
                                disabled={connecting}
                                startIcon={
                                    connecting ? (
                                        <CircularProgress size={14} sx={{ color: BRAND_ON_PRIMARY_FIXED }} />
                                    ) : (
                                        <LinkIcon />
                                    )
                                }
                                sx={{
                                    textTransform: 'none',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: BRAND_ON_PRIMARY_FIXED,
                                    background: `linear-gradient(90deg, ${BRAND_LUXE_PRIMARY} 0%, ${BRAND_PRIMARY_CONTAINER} 100%)`,
                                    borderRadius: 999,
                                    px: 2.5,
                                }}>
                                Connect Meta
                            </Button>
                        )}
                        <Button
                            onClick={() => void refresh()}
                            disabled={loading || connecting}
                            sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.45)', minWidth: 0 }}>
                            Refresh
                        </Button>
                    </Box>
                </Box>
            )}

            {error ? <Typography sx={{ fontSize: 12, color: '#ff8a65', mt: 1.5 }}>{error}</Typography> : null}

            {formDialog}
        </Box>
    );
}
